/**
 * Assets API client.
 * Users can upload, list, and delete media assets for use in videos.
 */
import { apiGet, apiPost, apiDelete, API_BASE_URL } from "./client.ts";
import { useAuthStore } from "@/stores/authStore.ts";

export interface Asset {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  url: string;
  category: string | null;
  tags: string[] | null;
  analysis: AssetAnalysis | null;
  createdAt: string;
}

export interface AssetAnalysis {
  summary: string;
  category: string;
  subject: string;
  colors: string[];
  mood: string;
  composition: string;
  visibleText: string | null;
  background: string;
  lighting: string;
  productFeatures: string[];
  referencePrompt: string;
}

export const assetsApi = {
  /** Upload a file (multipart form data) with progress callback. */
  upload: (
    file: File,
    category?: string,
    onProgress?: (pct: number) => void,
  ): Promise<{ asset: Asset }> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", file);
      if (category) formData.append("category", category);

      const token = useAuthStore.getState().token;
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE_URL}/assets/upload`);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error("Invalid response from server"));
          }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.message ?? `Upload failed (${xhr.status})`));
          } catch {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        }
      };

      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.onabort = () => reject(new Error("Upload cancelled"));

      xhr.send(formData);
    });
  },

  /** Import asset from URL. */
  uploadUrl: (body: { url: string; filename?: string; category?: string; tags?: string[] }) =>
    apiPost<{ asset: Asset }>("/assets/upload-url", body),

  /** List user's assets. */
  list: () => apiGet<{ assets: Asset[] }>("/assets"),

  /** Delete an asset. */
  delete: (id: string) => apiDelete<{ ok: boolean }>(`/assets/${id}`),
};
